import { X, Zap, Check, Heart } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useUpgradeModal } from '../../stores/upgradeModalStore'
import { useAuthStore } from '../../stores/authStore'

const FEATURE_LABELS: Record<string, { title: string; description: string }> = {
  difficulty_levels:  { title: 'Niveles de dificultad', description: 'Genera quizzes fáciles, medios o difíciles.' },
  detail_levels:      { title: 'Niveles de detalle',    description: 'Resúmenes ejecutivos, estándar o exhaustivos.' },
  source_references:  { title: 'Citas de fuente',        description: 'Explicaciones con referencia al material original.' },
  progress_chart:     { title: 'Progreso comparativo',  description: 'Gráfica de evolución entre intentos del mismo quiz.' },
  parent_dashboard:   { title: 'Dashboard de padres',   description: 'Monitorea el progreso de tus hijos.' },
  regenerate_quiz:    { title: 'Regenerar quiz',        description: 'Genera nuevos quizzes sin límite.' },
  unlimited_materials:{ title: 'Materiales ilimitados', description: 'Sube todos los materiales que necesites.' },
  history_permanent:  { title: 'Historial permanente',  description: 'Accede a todos tus resultados anteriores.' },
}

const PRO_BENEFITS = [
  'Materiales ilimitados',
  'Quiz con 3 niveles de dificultad',
  'Resúmenes ejecutivos y exhaustivos',
  'Explicaciones con citas del material',
  'Progreso comparativo entre intentos',
  'Dashboard para padres (hasta 5 hijos)',
  'Historial permanente',
]

export default function UpgradeModal() {
  const { isOpen, feature, close } = useUpgradeModal()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)

  if (!isOpen) return null

  const featureInfo = feature ? FEATURE_LABELS[feature] : null
  const isLinkedChild = !!user?.parentId

  const handleUpgrade = () => {
    close()
    navigate('/pricing')
  }

  // Child linked to a parent: show a different CTA instead of payment
  if (isLinkedChild) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
        onClick={(e) => { if (e.target === e.currentTarget) close() }}
      >
        <div
          className="glass-card w-full max-w-sm p-6 animate-slide-up text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)' }}
          >
            <Heart className="w-6 h-6 text-white" />
          </div>
          <h2 className="font-semibold text-lg mb-2" style={{ color: '#F1F5F9' }}>
            Función Pro
          </h2>
          {featureInfo && (
            <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>{featureInfo.description}</p>
          )}
          <div
            className="rounded-xl p-4 mb-5"
            style={{ background: 'rgba(79,70,229,0.08)', border: '1px solid rgba(79,70,229,0.2)' }}
          >
            <p className="text-sm" style={{ color: '#A5B4FC' }}>
              Pedile a tu papá o mamá que active <strong>Kapri Pro</strong> para que puedas usar esta función.
            </p>
          </div>
          <button onClick={close} className="btn-secondary w-full">
            Entendido
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) close() }}
    >
      <div
        className="glass-card w-full max-w-md p-6 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)' }}
            >
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold" style={{ color: '#F1F5F9' }}>
                Función Pro
              </h2>
              {featureInfo && (
                <p className="text-sm" style={{ color: '#64748B' }}>
                  {featureInfo.title}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={close}
            className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors"
            style={{ color: '#64748B' }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Feature description */}
        {featureInfo && (
          <div
            className="rounded-xl p-3.5 mb-5"
            style={{ background: 'rgba(79,70,229,0.08)', border: '1px solid rgba(79,70,229,0.2)' }}
          >
            <p className="text-sm" style={{ color: '#A5B4FC' }}>
              {featureInfo.description}
            </p>
          </div>
        )}

        {/* Benefits */}
        <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: '#475569' }}>
          Con Kapri Pro obtienes
        </p>
        <ul className="space-y-2 mb-6">
          {PRO_BENEFITS.slice(0, 5).map((benefit) => (
            <li key={benefit} className="flex items-center gap-2">
              <Check className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
              <span className="text-sm" style={{ color: '#CBD5E1' }}>{benefit}</span>
            </li>
          ))}
        </ul>

        {/* Pricing hint */}
        <div className="text-center mb-5">
          <span className="text-base line-through mr-2" style={{ color: '#64748B' }}>$24.99</span>
          <span className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>$14.95</span>
          <span className="text-sm ml-1" style={{ color: '#64748B' }}>/mes</span>
          <p className="text-xs mt-0.5" style={{ color: '#475569' }}>
            Cancelá cuando quieras
          </p>
        </div>

        {/* CTA */}
        <button onClick={handleUpgrade} className="btn-primary w-full flex items-center justify-center gap-2">
          <Zap className="w-4 h-4" />
          Ver planes y precios
        </button>
        <button
          onClick={close}
          className="w-full mt-2 py-2 text-sm transition-colors"
          style={{ color: '#64748B' }}
        >
          Quizás más tarde
        </button>
      </div>
    </div>
  )
}
